// ─── Recibir dinero · one-rail corridors (Brazil PIX · Mexico SPEI) ─────────────────────────────────────
// The COP panel's structure for a corridor with a single collection rail, as a side drawer:
//   0. Amount — ONE amount and, when it carries a commission, the "who covers it" switch; the rail card.
//   1. Payer — the corridor's own form (`payerStep`), its submit button in the drawer footer.
//   2. Confirmation — the money trail and the payer.
//   3. Processing (API call, drawer locked).
//   4. Result — the outcome banner, the charge, and the corridor's instrument (`instrument`): the PIX QR and
//      its copia e cola, or the CLABE lent to this charge. Live through `usePayinLiveStatus`.
//
// Each corridor is a config (`RailDepositCorridor`), so the two panels cannot drift apart in structure the way
// the two modals they replace did.

import { ArrowRight, ChevronLeft, CheckCircle2, Loader2, XCircle } from "lucide-react";
import { type ComponentType, type ReactNode, useEffect, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { newIdempotencyKey } from "@/modules/dashboard/dispersions/services/paymentsPayout.service";
import {
  isTerminalPayinStatus,
  usePayinLiveStatus,
} from "@/modules/dashboard/payins/hooks/usePayinLiveStatus";
import {
  type InitiatePayinPayload,
  type InitiatePayinResponse,
  type PayinRail,
  PaymentsPayinService,
} from "@/modules/dashboard/payins/services/paymentsPayin.service";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaChoiceCards } from "@/shared/components/forms/ColocaChoiceCards";
import { ColocaStepper } from "@/shared/components/navigation/ColocaStepper";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import {
  PayinAmountWithFee,
  type PayinAmountWithFeeState,
} from "@/shared/components/payments/PayinAmountWithFee";
import { type PayinConfirmRow, PayinConfirmSummary } from "@/shared/components/payments/PayinConfirmSummary";
import { quickAmountsFor } from "@/shared/components/payments/payinQuickAmounts";
import { RailMaintenanceNotice } from "@/shared/components/payments/RailMaintenanceNotice";
import { useAvailableCorridors } from "@/shared/hooks/useAvailableCorridors";
import { useTaxLabel } from "@/shared/hooks/useTaxLabel";
import type { WalletsTranslations } from "@/shared/lang/interfaces/wallets";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import type { LimitPayerType } from "@/shared/services/payments/paymentCorridors.service";
import type { UserEntity } from "@/shared/types/user.entity";
import { boundsForPayer, formatLimitAmount, resolveAmountLimitViolation } from "@/shared/utils/amountLimits";
import { formatCurrencyAmount } from "@/shared/utils/currencyMinorUnits";
import { compareDecimalStrings, isDecimalString } from "@/shared/utils/decimal";
import { splitFeeTotal } from "@/shared/utils/paymentTaxes";
import { formatRail } from "@/shared/utils/railDisplay";

import { CURRENCY_TO_COUNTRY } from "../../constants/wallets.constants";
import { useDepositAmountLabels, useDepositFeeSwitchLabels } from "../../hooks/useDepositAmountLabels";
import type { Wallet } from "../../types/wallets-api.types";
import { DepositOutcomeBanner } from "./DepositOutcomeBanner";

const STEP = { AMOUNT: 0, PAYER: 1, CONFIRM: 2, PROCESSING: 3, RESULT: 4 } as const;
type DepositStep = (typeof STEP)[keyof typeof STEP];

type WalletsKey = keyof WalletsTranslations;

export interface DepositPayerStepProps<P> {
  /** HTML id of the form — the drawer renders its submit button in the footer, wired with `form={formId}`. */
  formId: string;
  defaults: P;
  /** The priced amount, already drawn, shown above the fields so the payer step says what is being charged. */
  amount: ReactNode;
  /** Fires only with a valid payer. */
  onSubmit: (values: P) => void;
}

export interface PaymentInstrumentProps {
  result: InitiatePayinResponse;
  /** What the payer sends, formatted with its currency. */
  amount: string;
  /** The same figure as a plain decimal (`1500.00`), for pasting into a banking app. */
  rawAmount: string;
  onReset: () => void;
}

export interface RailDepositCorridor<P> {
  rail: PayinRail;
  country: string;
  currency: string;
  /** Under "Cobrar un monto": how the payer pays on this rail. */
  amountHintKey: WalletsKey;
  /** Under the rail's name on its card. */
  methodHintKey: WalletsKey;
  /** While the charge is created: what is being made (a QR, a CLABE). */
  processingKey: WalletsKey;
  methodIcon: ReactNode;
  /** Seeded from the signed-in user: on this panel they are topping up their own wallet. */
  initialPayer: (user: UserEntity | null | undefined) => P;
  PayerStep: ComponentType<DepositPayerStepProps<P>>;
  /** The payer's rows on the confirmation screen, already translated. */
  payerRows: (payer: P, t: (key: WalletsKey) => string) => PayinConfirmRow[];
  /** The payer kind the corridor limits are checked against, once the payer is known. */
  payerType: (payer: P) => LimitPayerType | null;
  buildPayload: (input: {
    organizationId: string;
    grossAmount: string;
    payer: P;
    idempotencyKey: string;
  }) => InitiatePayinPayload;
  Instrument: ComponentType<PaymentInstrumentProps>;
}

export interface RailDepositDrawerProps<P> {
  wallet: Wallet;
  open: boolean;
  onClose: () => void;
  corridor: RailDepositCorridor<P>;
}

const EMPTY_PRICED: PayinAmountWithFeeState = {
  mode: "gross",
  preview: null,
  amount: null,
  loading: false,
  error: null,
};

/** A decimal string cut to cents, for a banking app's amount field: `1500.5` → `1500.50`. */
const plainAmount = (value: string): string => {
  const [integer, fraction = ""] = value.split(".");
  return `${integer}.${fraction.padEnd(2, "0").slice(0, 2)}`;
};

function StepProcessing({
  payload,
  label,
  onSuccess,
  onError,
}: {
  payload: InitiatePayinPayload;
  label: string;
  onSuccess: (data: InitiatePayinResponse) => void;
  onError: (message: string) => void;
}) {
  useEffect(() => {
    let cancelled = false;
    // `initiatePayinOnce` is what makes this effect safe under StrictMode's double mount — see the service.
    PaymentsPayinService.initiatePayinOnce(payload)
      .then((res) => {
        if (!cancelled) onSuccess(res.data);
      })
      .catch((err: unknown) => {
        if (!cancelled) onError(HttpApiService.getErrorMessage(err));
      });
    return () => {
      cancelled = true;
    };
    // Fires once per mount — payload is stable; the service dedupes the POST.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col items-center justify-center gap-4 py-16">
      <Loader2 className="size-10 animate-spin text-primary motion-reduce:animate-none" aria-hidden="true" />
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

export function RailDepositDrawer<P>({ wallet, open, onClose, corridor }: RailDepositDrawerProps<P>) {
  const { t, activeLanguage } = useTranslations("wallets");
  const taxLabel = useTaxLabel();
  const { t: tPayins } = useTranslations("payins");
  const { user } = useAuth();
  const labels = useDepositAmountLabels();
  const feeSwitch = useDepositFeeSwitchLabels();
  const { rail, country, currency, PayerStep, Instrument } = corridor;
  const formId = `deposit-payer-${rail.toLowerCase()}`;

  const [step, setStep] = useState<DepositStep>(STEP.AMOUNT);
  const [priced, setPriced] = useState<PayinAmountWithFeeState>(EMPTY_PRICED);
  const [payer, setPayer] = useState<P | null>(null);
  const [payinResult, setPayinResult] = useState<InitiatePayinResponse | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // State (not a ref): it is read during render to mount StepProcessing.
  const [pendingPayload, setPendingPayload] = useState<InitiatePayinPayload | null>(null);
  // One per attempt: a retry of the same confirmation replays instead of charging twice.
  const [idempotencyKey, setIdempotencyKey] = useState<string>(() => newIdempotencyKey());

  const organizationId = user?.organization_id ?? user?.organization?.id ?? null;
  const countryName = CURRENCY_TO_COUNTRY[wallet.currency] ?? wallet.country;
  const money = (value: string | null | undefined) =>
    `${formatCurrencyAmount(value, currency, activeLanguage)} ${currency}`;

  // The amount is typed before the payer, so that step checks the envelope of both payer kinds; the exact
  // cap of the kind the payer turned out to be is re-checked on confirm.
  const corridors = useAvailableCorridors(organizationId ?? undefined);
  const railAvailable = corridors.isAvailable(rail, country, currency, "PAYIN");
  const railInMaintenance = corridors.inMaintenance({ rail, country, currency, direction: "PAYIN" });
  const limits = corridors.limitsFor(rail, country, currency, "PAYIN");
  const payerType = payer ? corridor.payerType(payer) : null;
  const confirmBounds = boundsForPayer(limits, payerType);
  const confirmViolation = priced.preview
    ? resolveAmountLimitViolation(priced.preview.gross_amount, confirmBounds)
    : null;

  const liveStatus = usePayinLiveStatus(payinResult?.id ?? null, { expiresAt: payinResult?.expires_at });
  const isTerminal = isTerminalPayinStatus(liveStatus);

  const resetState = () => {
    setStep(STEP.AMOUNT);
    setPriced(EMPTY_PRICED);
    setPayer(null);
    setPayinResult(null);
    setSubmitError(null);
    setPendingPayload(null);
    setIdempotencyKey(newIdempotencyKey());
  };

  const resetAndClose = () => {
    resetState();
    onClose();
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && step === STEP.PROCESSING) return; // locked during the API call
    if (!next) resetAndClose();
  };

  const handleConfirm = () => {
    if (confirmViolation || !payer || !priced.preview) return;
    // The runtime scopes the payin by organization — without one the DTO is unbuildable.
    if (!organizationId) {
      setSubmitError(t("deposit_error_no_org"));
      setStep(STEP.RESULT);
      return;
    }
    setPendingPayload(
      corridor.buildPayload({
        organizationId,
        // The preview's resolved gross — the amount whose commission was just confirmed.
        grossAmount: priced.preview.gross_amount,
        payer,
        idempotencyKey,
      }),
    );
    setStep(STEP.PROCESSING);
  };

  const pricedAmount = priced.preview ? (
    <div className="flex items-baseline justify-between gap-4 rounded-xl border border-border bg-muted/40 px-4 py-3">
      <span className="text-sm text-muted-foreground">{t("deposit_payer_sends")}</span>
      <span className="font-mono text-base font-semibold text-foreground tabular-nums">
        {money(priced.preview.gross_amount)}
      </span>
    </div>
  ) : null;

  const body = (() => {
    if (step === STEP.AMOUNT && railInMaintenance) {
      return <RailMaintenanceNotice kind="receive" country={country} />;
    }
    if (step === STEP.AMOUNT) {
      return (
        <div className="space-y-5">
          <div>
            <h3 className="text-sm font-semibold text-foreground">{t("deposit_amount_section_title")}</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">{t(corridor.amountHintKey)}</p>
          </div>
          <PayinAmountWithFee
            organizationId={organizationId}
            rail={rail}
            country={country}
            currency={currency}
            labels={labels}
            feeSwitch={feeSwitch}
            quickAmounts={quickAmountsFor(currency)}
            variant="prominent"
            autoFocus
            initialAmount={priced.amount}
            initialMode={priced.mode}
            payerType={payerType}
            onChange={setPriced}
          />
          <ColocaChoiceCards
            value={rail}
            onValueChange={() => undefined}
            columns={1}
            aria-label={t("deposit_confirm_method_row")}
            items={[
              {
                value: rail,
                label: formatRail(rail),
                description: railAvailable ? t(corridor.methodHintKey) : tPayins("method_unavailable"),
                icon: corridor.methodIcon,
                disabled: !railAvailable,
              },
            ]}
          />
        </div>
      );
    }

    if (step === STEP.PAYER) {
      return (
        <PayerStep
          formId={formId}
          defaults={payer ?? corridor.initialPayer(user)}
          amount={pricedAmount}
          onSubmit={(values) => {
            setPayer(values);
            setStep(STEP.CONFIRM);
          }}
        />
      );
    }

    if (step === STEP.CONFIRM && priced.preview && payer) {
      const fee = splitFeeTotal(priced.preview.fee_total, priced.preview.taxes);
      const rows: PayinConfirmRow[] = [
        { label: t("deposit_payer_sends"), value: money(priced.preview.gross_amount) },
        { label: t("deposit_breakdown_fee"), value: money(fee.commission) },
        ...fee.taxes.map((tax) => ({ label: taxLabel(tax), value: money(tax.amount) })),
        { label: t("deposit_row_credited"), value: money(priced.preview.net_amount) },
        { label: t("deposit_confirm_method_row"), value: formatRail(rail) },
        ...corridor.payerRows(payer, t),
      ];
      return (
        <div className="space-y-4">
          {confirmViolation && confirmBounds && (
            <ColocaAlert
              variant="destructive"
              text={tPayins(
                confirmViolation === "above_maximum" ? "confirm_limit_above" : "confirm_limit_below",
                {
                  payer: tPayins(payerType === "COMPANY" ? "limit_payer_company" : "limit_payer_person"),
                  max: formatLimitAmount(confirmBounds.max_amount ?? "0", currency, activeLanguage),
                  min: formatLimitAmount(confirmBounds.min_amount ?? "0", currency, activeLanguage),
                },
              )}
              actions={
                <ColocaButton size="sm" variant="default" onClick={() => setStep(STEP.AMOUNT)}>
                  {tPayins("confirm_limit_edit_amount")}
                </ColocaButton>
              }
            />
          )}
          <PayinConfirmSummary title={t("deposit_confirm_amount")} rows={rows} />
        </div>
      );
    }

    if (step === STEP.PROCESSING && pendingPayload) {
      return (
        <StepProcessing
          payload={pendingPayload}
          label={t(corridor.processingKey)}
          onSuccess={(data) => {
            setPayinResult(data);
            setStep(STEP.RESULT);
          }}
          onError={(message) => {
            setSubmitError(message);
            setStep(STEP.RESULT);
          }}
        />
      );
    }

    if (step === STEP.RESULT && submitError) {
      return (
        <div className="flex flex-col items-center gap-4 py-8 text-center">
          <div className="flex size-14 items-center justify-center rounded-full bg-destructive/10">
            <XCircle className="size-7 text-destructive" aria-hidden="true" />
          </div>
          <div>
            <p className="font-semibold text-destructive">{t("deposit_result_error")}</p>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">{submitError}</p>
          </div>
          <div className="flex gap-2 pt-2">
            <ColocaButton variant="ghost" onClick={resetAndClose}>
              {t("deposit_close")}
            </ColocaButton>
            <ColocaButton variant="primary" onClick={resetState}>
              {t("deposit_retry")}
            </ColocaButton>
          </div>
        </div>
      );
    }

    if (step === STEP.RESULT && payinResult) {
      const resultFee = splitFeeTotal(payinResult.fee_total, payinResult.taxes);
      // Decided on the decimal string: a readable zero, never merely an unreadable value.
      const feeIsZero =
        isDecimalString(payinResult.fee_total ?? "") &&
        compareDecimalStrings(payinResult.fee_total ?? "", "0") === 0;
      return (
        <div className="space-y-4">
          <DepositOutcomeBanner status={liveStatus} />
          <div className="flex items-start gap-3 rounded-xl border border-primary/25 bg-primary/5 p-4">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary">
              <CheckCircle2 className="size-5 text-primary-foreground" aria-hidden="true" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-primary">{t("deposit_payment_created")}</p>
              <p className="mt-0.5 font-mono text-xs break-all text-muted-foreground">
                {t("deposit_result_id")} · {payinResult.id}
              </p>
              <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
                <dt className="text-muted-foreground">{t("deposit_payer_sends")}</dt>
                <dd className="text-right font-mono font-semibold text-foreground">
                  {money(payinResult.gross_amount)}
                </dd>
                <dt className="text-muted-foreground">{t("deposit_result_fee_label")}</dt>
                <dd className="text-right font-mono text-foreground">
                  {feeIsZero ? t("deposit_result_fee_free") : money(resultFee.commission)}
                </dd>
                {resultFee.taxes.map((tax) => (
                  <div key={tax.code} className="contents">
                    <dt className="text-muted-foreground">{taxLabel(tax)}</dt>
                    <dd className="text-right font-mono text-foreground">{money(tax.amount)}</dd>
                  </div>
                ))}
                <dt className="text-muted-foreground">{t("deposit_row_credited")}</dt>
                <dd className="text-right font-mono text-foreground">{money(payinResult.net_amount)}</dd>
              </dl>
            </div>
          </div>

          {!isTerminal && (
            <Instrument
              result={payinResult}
              amount={money(payinResult.gross_amount)}
              rawAmount={plainAmount(payinResult.gross_amount)}
              onReset={resetState}
            />
          )}

          {/* The result owns its actions: the drawer has no footer on this step. */}
          <div className="flex justify-between border-t border-border pt-4">
            <ColocaButton variant="ghost" onClick={resetState}>
              {t("deposit_new")}
            </ColocaButton>
            <ColocaButton variant="primary" onClick={resetAndClose}>
              {t("deposit_close")}
            </ColocaButton>
          </div>
        </div>
      );
    }

    return null;
  })();

  const footer = (() => {
    if (step === STEP.AMOUNT && railInMaintenance) {
      return (
        <ColocaButton variant="ghost" onClick={resetAndClose}>
          {t("deposit_close")}
        </ColocaButton>
      );
    }
    if (step === STEP.AMOUNT) {
      return (
        <>
          <ColocaButton variant="ghost" onClick={resetAndClose}>
            {t("deposit_close")}
          </ColocaButton>
          {/* Keyed apart from the payer step's submit button, which takes this same slot: React would reuse
              the element, it turns into `type="submit"` during this very click, and the click's default action
              then submits the still-empty payer form, which opens with its errors already showing. */}
          <ColocaButton
            key="amount-continue"
            variant="primary"
            iconTrailing={ArrowRight}
            disabled={!priced.preview || !railAvailable}
            onClick={() => setStep(STEP.PAYER)}
          >
            {t("deposit_continue")}
          </ColocaButton>
        </>
      );
    }
    if (step === STEP.PAYER) {
      return (
        <>
          <ColocaButton variant="ghost" icon={ChevronLeft} onClick={() => setStep(STEP.AMOUNT)}>
            {t("deposit_edit_amount")}
          </ColocaButton>
          <ColocaButton
            key="payer-submit"
            type="submit"
            form={formId}
            variant="primary"
            iconTrailing={ArrowRight}
          >
            {t("deposit_continue")}
          </ColocaButton>
        </>
      );
    }
    if (step === STEP.CONFIRM) {
      return (
        <>
          <ColocaButton variant="ghost" icon={ChevronLeft} onClick={() => setStep(STEP.PAYER)}>
            {t("deposit_edit")}
          </ColocaButton>
          <ColocaButton variant="primary" onClick={handleConfirm} disabled={!!confirmViolation}>
            {t("deposit_create_payment")}
          </ColocaButton>
        </>
      );
    }
    // Processing is locked, and the result owns its own actions.
    return undefined;
  })();

  return (
    <ColocaDrawer
      open={open}
      onOpenChange={handleOpenChange}
      size="lg"
      showCloseButton={step !== STEP.PROCESSING}
      title={t("card_action_load")}
      description={`${countryName} · ${wallet.currency}`}
      footer={footer}
      pinLastChild={!footer}
      // Fixed under the header so the payer step's scroll never hides which step this is.
      toolbar={
        step > STEP.AMOUNT &&
        step <= STEP.CONFIRM && (
          <ColocaStepper
            steps={[
              { label: tPayins("step_amount") },
              { label: tPayins("step_payer") },
              { label: tPayins("step_confirm") },
            ]}
            currentStep={step}
            aria-label={t("deposit_modal_title")}
          />
        )
      }
    >
      {body}
    </ColocaDrawer>
  );
}
